import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App, ConfigProvider, theme } from "antd";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { FlagsProvider } from "@/contexts/flags-context";
import { getRouter } from "./router";
import "./styles.css";
import "antd/dist/antd.css";
import { RouterProvider } from "@tanstack/react-router";
import { WebStorageStateStore } from "oidc-client-ts";
import { AuthProvider } from "react-oidc-context";
import { isOnboardingDisabled } from "@/common/onboarding";
import { initSentry } from "@/common/sentry";
import {
	ForbiddenError,
	NotAuthorizedError,
	ServerError,
} from "@/common/use-gql-client";

const authRedirectUri: string =
	import.meta.env.VITE_AUTH_REDIRECT_URI ?? window.location.origin;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Auth errors won't fix themselves — fail fast instead of retrying 3x.
			retry: (failureCount, error) =>
				error instanceof ForbiddenError || error instanceof NotAuthorizedError
					? false
					: failureCount < 3,
			throwOnError: (error) =>
				error instanceof ForbiddenError ||
				error instanceof NotAuthorizedError ||
				error instanceof ServerError,
		},
	},
});

// Read before the router runs, so `?onboarding=off` is captured before any redirect drops it.
const flags = { ONBOARDING_DISABLED: isOnboardingDisabled() };

const router = getRouter();
const doc = document.getElementById("app");
if (!doc) throw new Error("app element not found");

createRoot(doc).render(
	<StrictMode>
		<ConfigProvider
			theme={{
				algorithm: theme.defaultAlgorithm,
				token: {
					// Brand kit: brand-kit-instruction.md in stratonext/glassdoor.
					// Typography: Inter (self-hosted, see styles.css); page titles use
					// Oxanium via styles.css.
					fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, sans-serif`,
					fontSize: 14,

					// colorBgContainer: "white",
					colorBgLayout: "white",

					// Actions and links use Dark Blue (#1864FF, 4.9:1 on white); Blue
					// (#1890FF) is only 3.2:1, so it stays for info/highlights.
					colorPrimary: "#1864ff",
					colorLink: "#1864ff",
					colorInfo: "#1890ff",
					colorText: "#0a1633", // Ink
					colorTextSecondary: "#5b6782", // Slate
					colorBorderSecondary: "#dce6f3", // Line
					colorFillSecondary: "#f3f7fc", // Mist
					colorSuccess: "#52ca16",
					colorWarning: "#f7bf4f",
					colorError: "#f73e41",
					borderRadius: 8,
					wireframe: false,
				},

				components: {
					// Denser alerts with a prominent full-strength colored border and
					// smaller icon (AWS-console style). Border tokens are scoped to Alert
					// only — the global colorXxxBorder tints are unchanged elsewhere.
					Alert: {
						defaultPadding: "6px 12px",
						withDescriptionPadding: "10px 12px",
						withDescriptionIconSize: 16,
						colorErrorBorder: "#f73e41",
						colorWarningBorder: "#f7bf4f",
						colorSuccessBorder: "#52ca16",
						colorInfoBorder: "#1890ff",
					},
					// Titles carry a large default top margin (~1.2em) — drop it so a Title
					// used as a page/section heading sits flush; use layout gap for spacing.
					Typography: { titleMarginTop: 0 },
					Layout: { headerBg: "#0a1633", headerColor: "white" }, // Ink
					Descriptions: { labelColor: "#5b6782", itemPaddingBottom: 8 }, // Slate
					Table: {
						headerBg: "#f3f7fc", // Mist
						cellPaddingBlock: 8,
						cellPaddingBlockSM: 4,
					},
					Segmented: {
						itemSelectedBg: "white",
						trackBg: "#f3f7fc", // Mist
					},
					Menu: {
						itemBg: "transparent",
						subMenuItemBg: "transparent",
						popupBg: "white",
						collapsedIconSize: 20,
						iconSize: 16,
					},
				},
			}}
		>
			<AuthProvider
				authority={import.meta.env.VITE_AUTH_AUTHORITY}
				client_id={import.meta.env.VITE_AUTH_CLIENT_ID}
				redirect_uri={authRedirectUri}
				// Only the redirect URI is a sign-in callback. Other pages can carry their own
				// ?code=&state= (the GitHub install return to workspace settings); treating those as
				// a Cognito callback fails the state lookup and drops an existing session to /login.
				skipSigninCallback={
					window.location.pathname !== new URL(authRedirectUri).pathname
				}
				post_logout_redirect_uri={
					import.meta.env.VITE_AUTH_POST_LOGOUT_REDIRECT_URI ??
					window.location.origin
				}
				scope={import.meta.env.VITE_AUTH_SCOPE ?? "openid email profile"}
				// localStorage (default is sessionStorage) so deep links opened in a new tab
				// reuse the existing session instead of bouncing to /login.
				userStore={new WebStorageStateStore({ store: window.localStorage })}
				automaticSilentRenew={true}
				// // Skip the Cognito hosted-UI provider chooser and go straight to Google
				// // (the pool's user-facing clients only allow Google).
				// extraQueryParams={{ identity_provider: "Google" }}
			>
				<App>
					<QueryClientProvider client={queryClient}>
						<FlagsProvider flags={flags}>
							<RouterProvider router={router} />
						</FlagsProvider>
					</QueryClientProvider>
				</App>
			</AuthProvider>
		</ConfigProvider>
	</StrictMode>,
);

initSentry();
